import { Box, capitalize, Grid, Typography } from "@mui/material";
import { differenceInCalendarWeeks, endOfMonth, endOfWeek, format, getDay, parse, startOfMonth, startOfWeek, } from "date-fns";
import { fi } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, Event as RBCEvent, } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import getLocale from "../../../common/helpers/getLocale";
import { fetchCalendarEvents, selectCalendarEvents, selectIsLoading, } from "./calendarSlice";
import { CalendarEventComponent } from "./components/CalendarEvent";
import { CalendarThemeWrapper } from "./components/CalendarThemeWrapper";
import { CalendarToolbar } from "./components/CalendarToolbar";
import "./styles/calendar.css";
import { StyledTalentCalendarPaper } from "./styles/styles";
import { calenarEntryRequestTypes } from "./types";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales: { fi },
});

interface Props {
  talentId: string;
}

const TOOLBAR_HEIGHT_PX = 68;
const HEADER_ROW_HEIGHT_PX = 46;
const ROW_MIN_HEIGHT_PX = 115;

const getWeeksInMonth = (date: Date): number => {
  const calStart = startOfWeek(startOfMonth(date), { weekStartsOn: 1 });
  const calEnd = endOfWeek(endOfMonth(date), { weekStartsOn: 1 });
  return differenceInCalendarWeeks(calEnd, calStart, { weekStartsOn: 1 }) + 1;
}

export const TalentCalendar = ({ talentId }: Props) => {
  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();
  const events = useAppSelector(selectCalendarEvents);
  const loading = useAppSelector(selectIsLoading);
  const [currentDate, setCurrentDate] = useState(new Date());

  const calendarHeight = useMemo(() => {
    const numWeeks = getWeeksInMonth(currentDate);
    return TOOLBAR_HEIGHT_PX + HEADER_ROW_HEIGHT_PX + numWeeks * ROW_MIN_HEIGHT_PX;
  }, [currentDate]);

  useEffect(() => {
    const from = format(startOfMonth(currentDate), isoFormat);
    const to = format(endOfMonth(currentDate), isoFormat);
    const types = calenarEntryRequestTypes.join(",");

    dispatch(
      fetchCalendarEvents({
        talentId,
        from,
        to,
        types,
      }),
    );
  }, [dispatch, talentId, currentDate]);

  const eventPropGetter = (event: RBCEvent) => {
    const calendarEvent = event as any;
    const base = `calendar-event-${calendarEvent.type.toLowerCase()}`;
    const type =
      calendarEvent.type === "UnavailabilityEntry" &&
      calendarEvent.unavailabilityType
        ? ` ${base}-${calendarEvent.unavailabilityType.toLowerCase()}`
        : "";
    const className = `${base}${type}`;
    return { className, style: { borderRadius: 12 } };
  };

  const handleNavigate = (newDate: Date) => {
    setCurrentDate(newDate);
  };

  // Custom formats for calendar
  const formats = {
    weekdayFormat: (date: Date) =>
      format(date, "EEEEEE", { locale: getLocale() }),
    monthHeaderFormat: (date: Date) =>
      capitalize(format(date, "LLLL yyyy", { locale: getLocale() })),
    dayHeaderFormat: (date: Date) =>
      capitalize(format(date, "cccc d.M.yyyy", { locale: getLocale() })),
  };

  return (
    <Grid container>
      <Grid item xs={12} md={12} lg={12} xl={7} mt={1} mb={3}>
        <Typography variant="secondary" maxWidth="600px">
          {t("calendar.calendarGuideText")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={12} lg={12} xl={10} sx={{ position: "relative" }}>
        <StyledTalentCalendarPaper
          id="talent-calendar-container"
          elevation={0}
        >
          <CalendarThemeWrapper>
            <OverlaySpinner isLoading={loading} sx={{ position: "absolute" }} />
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Calendar
                popup={true}
                localizer={localizer}
                events={events}
                date={currentDate}
                onNavigate={handleNavigate}
                onSelectEvent={() => undefined}
                view="month"
                views={["month"]}
                components={{
                  toolbar: CalendarToolbar,
                  event: CalendarEventComponent,
                }}
                eventPropGetter={eventPropGetter}
                formats={formats}
                culture="fi"
                messages={{
                  today: t("calendar.today"),
                  previous: t("calendar.previous"),
                  next: t("calendar.next"),
                  month: t("calendar.month"),
                  showMore: (total) => t("calendar.showMore", { count: total }),
                }}
                style={{ height: calendarHeight }}
              />
            </Box>
          </CalendarThemeWrapper>
        </StyledTalentCalendarPaper>
      </Grid>
    </Grid>
  );
};

export default TalentCalendar;
